import { ForgotPasswordModal } from "@/components/auth/forgot-password-modal";
import { Button } from "@/components/core/buttons/button";
import { Input } from "@/components/core/forms/input";
import { useModal } from "@/components/core/modals/use-modal";
import { useAuth } from "@/hooks/use-auth";
import { useValidationErrors } from "@/hooks/use-validation-errors";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const SSO_URL = import.meta.env.VITE_SSO_URL || "http://localhost:8001";

export const Route = createFileRoute("/login")({
	component: LoginPage,
});

function LoginPage() {
	const navigate = useNavigate();
	const { login, user } = useAuth();
	const [isLoading, setIsLoading] = useState(false);
	const modal = useModal();
	const { errors, setErrors, clearErrors } = useValidationErrors();

	const [formData, setFormData] = useState({
		email: "",
		password: "",
	});

	// Redirect if already logged in
	useEffect(() => {
		if (user) {
			navigate({ to: "/sales-dashboard" });
		}
	}, [user, navigate]);

	const handleSsoLogin = () => {
		const callbackUrl = `${window.location.origin}/sso/authenticate`;
		window.location.href = `${SSO_URL}`;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		clearErrors();
		setIsLoading(true);

		try {
			await login({
				email: formData.email,
				password: formData.password,
			});

			toast.success("Login successful!");
			navigate({ to: "/sites" });
			// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		} catch (error: any) {
			if (error?.response?.status === 422) {
				setErrors(error.response.data.errors || {});
				toast.error("Please check your credentials and try again.");
			} else if (error?.response?.status === 401) {
				toast.error("Invalid email or password.");
			} else {
				toast.error("An error occurred. Please try again.");
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
		// Clear error for this field when user types
		if (errors[name]) {
			setErrors((prev) => {
				const newErrors = { ...prev };
				delete newErrors[name];
				return newErrors;
			});
		}
	};

	const handleForgotPassword = () => {
		modal.openModal({
			title: "Reset Password",
			description: "Enter your email address and we'll send you a link to reset your password.",
			component: <ForgotPasswordModal onSuccess={() => {}} />,
		});
	};

	return (
		<div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
			<div className="max-w-md w-full space-y-8">
				<div>
					<div className="mx-auto rounded-lg flex items-center justify-center p-6">
						<img className="h-full w-full object-contain" src="/salesmon-logo.png" alt="Salesmon" />
					</div>
					<h2 className="mt-6 text-center text-3xl font-extrabold ">Sign in to your account</h2>
				</div>

				{/* SSO Login */}
				<div>
					<Button type="button" className="group relative w-full flex justify-center" variant="default" onClick={handleSsoLogin}>
						Sign in with REVLV SSO
					</Button>
				</div>

				<div className="relative">
					<div className="absolute inset-0 flex items-center">
						<div className="w-full border-t border-gray-300" />
					</div>
					<div className="relative flex justify-center text-sm">
						<span className="px-2 bg-white text-gray-500">Or sign in with email</span>
					</div>
				</div>

				<form className="space-y-6" onSubmit={handleSubmit}>
					<div className="rounded-md shadow-sm -space-y-px">
						<div>
							<label htmlFor="email" className="sr-only">
								Email address
							</label>
							<Input
								id="email"
								name="email"
								type="email"
								autoComplete="email"
								required
								className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
								placeholder="Email address"
								value={formData.email}
								onChange={handleInputChange}
								disabled={isLoading}
							/>
							{errors.email && <p className="mt-1 text-sm text-red-600">{errors.email[0]}</p>}
						</div>

						<div>
							<label htmlFor="password" className="sr-only">
								Password
							</label>
							<Input
								id="password"
								name="password"
								type="password"
								autoComplete="current-password"
								required
								className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
								placeholder="Password"
								value={formData.password}
								onChange={handleInputChange}
								disabled={isLoading}
							/>
							{errors.password && <p className="mt-1 text-sm text-red-600">{errors.password[0]}</p>}
						</div>
					</div>

					<div>
						<Button type="submit" variant="outline" className="group relative w-full flex justify-center" loading={isLoading} disabled={isLoading}>
							Sign in
						</Button>
					</div>

					<div className="flex items-center justify-between">
						<button type="button" onClick={handleForgotPassword} className="text-sm text-primary-600 hover:text-primary-400">
							Forgot your password?
						</button>
						<Link to="/register" className="text-sm text-primary-600 hover:text-primary-400">
							Don't have an account? Register
						</Link>
					</div>
				</form>
			</div>
		</div>
	);
}
